<template>
  <div>
    <div class="mb-4">
      <div class="subtitle-2 mb-2">{{ $t('routePlanning.dialogs.allocatePeopleToLines.bulkApplyTitle') }}</div>
      <v-row dense align="center">
        <v-col cols="12" md="4">
          <v-autocomplete
            :value="bulk.id_grupo"
            :items="bulkGroupItems"
            :label="$t('routePlanning.fields.selectGroup')"
            item-text="nome"
            item-value="id_grupo"
            clearable
            dense
            hide-details
            :disabled="!selection.length"
            @input="$emit('update:bulk', { ...bulk, id_grupo: $event })"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-autocomplete
            :value="bulk.id_linha_ida"
            :items="bulkLineItemsIda"
            :label="lineDirectionLabel('ida')"
            :item-text="lineLabel"
            item-value="id_linha"
            clearable
            dense
            hide-details
            :disabled="!selection.length"
            @input="$emit('update:bulk', { ...bulk, id_linha_ida: $event })"
            @change="$emit('bulk-line-change', 'ida')"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-autocomplete
            :value="bulk.id_linha_volta"
            :items="bulkLineItemsVolta"
            :label="lineDirectionLabel('volta')"
            :item-text="lineLabel"
            item-value="id_linha"
            clearable
            dense
            hide-details
            :disabled="!selection.length"
            @input="$emit('update:bulk', { ...bulk, id_linha_volta: $event })"
            @change="$emit('bulk-line-change', 'volta')"
          />
        </v-col>
        <v-col cols="12" md="4" class="d-flex align-center">
          <v-btn
            color="primary"
            :outlined="hideActions"
            dark
            :disabled="!canApplyBulk"
            @click="$emit('apply-bulk')"
          >{{ $t('routePlanning.dialogs.allocatePeopleToLines.bulkApplyButton') }}</v-btn>
        </v-col>
      </v-row>
      <div
        v-if="!selection.length"
        class="caption grey--text text--darken-1 mt-2"
      >{{ $t('routePlanning.dialogs.allocatePeopleToLines.bulkApplyHint') }}</div>
    </div>
    <section v-if="publicationLayout" class="allocation-publication-list" aria-label="Alocação de pessoas">
      <header class="allocation-publication-list__header">
        <div>
          <div class="subtitle-1 font-weight-bold">{{ $t('routePlanning.modules.alocation') }}</div>
          <div class="caption grey--text text--darken-1">
            {{ items.length }} pessoa(s) · {{ selection.length }} selecionada(s)
          </div>
        </div>
        <v-checkbox
          :input-value="allItemsSelected"
          :indeterminate="someItemsSelected"
          label="Selecionar todos"
          dense
          hide-details
          class="mt-0"
          @change="toggleAllItems"
        />
      </header>

      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <div v-else class="allocation-publication-list__body">
        <article
          v-for="item in items"
          :key="item.id_pessoa"
          class="allocation-person-card"
          :class="{ 'allocation-person-card--selected': isItemSelected(item) }"
        >
          <header class="allocation-person-card__header">
            <v-checkbox
              :input-value="isItemSelected(item)"
              dense
              hide-details
              class="mt-0 pt-0"
              @change="toggleItemSelection(item, $event)"
            />
            <div class="allocation-person-card__identity">
              <strong>{{ item.nome || '-' }}</strong>
              <span>{{ item.matricula || '-' }}</span>
            </div>
            <div class="allocation-person-card__meta">
              <v-chip x-small outlined>{{ item.nome_situacao_validacao || '-' }}</v-chip>
              <v-chip x-small outlined color="primary">{{ item.cod_unidade || '-' }}</v-chip>
              <v-chip
                v-if="Number(item.pcd) === 1 || item.pcd === true"
                x-small
                outlined
                color="deep-purple"
              >{{ $t('routePlanning.fields.pcd') }}</v-chip>
              <v-chip
                v-if="isRejectedItem(item) && hasNearestLineDistance(item)"
                x-small
                outlined
                color="error"
              >{{ nearestLineDistanceChip(item) }}</v-chip>
              <v-chip
                v-if="isRejectedItem(item) && hasNearestLineName(item)"
                x-small
                outlined
                color="error"
              >{{ nearestLineNameChip(item) }}</v-chip>
            </div>
          </header>

          <div
            v-if="commonRoundTripLines(item).length"
            class="allocation-round-trip-action"
            :class="{ 'allocation-round-trip-action--applied': sameRoundTripLineApplied(item) }"
          >
            <div class="allocation-round-trip-action__text">
              <v-icon small :color="sameRoundTripLineApplied(item) ? 'success' : 'primary'">
                {{ sameRoundTripLineApplied(item) ? 'mdi-check-circle' : 'mdi-swap-horizontal' }}
              </v-icon>
              <span>{{ $t(sameRoundTripLineApplied(item)
                ? 'routePlanning.dialogs.allocatePeopleToLines.sameLineBothDirectionsApplied'
                : 'routePlanning.dialogs.allocatePeopleToLines.sameLineBothDirectionsHint') }}</span>
            </div>
            <v-btn
              v-if="!sameRoundTripLineApplied(item)"
              small
              outlined
              color="primary"
              @click="useSameLineBothDirections(item)"
            >
              {{ $t('routePlanning.dialogs.allocatePeopleToLines.sameLineBothDirectionsAction') }}
            </v-btn>
          </div>

          <div class="allocation-person-card__content">
            <div class="allocation-person-card__group">
              <div class="allocation-field-heading">
                <v-icon small>mdi-account-group-outline</v-icon>
                {{ $t('routePlanning.fields.selectGroup') }}
              </div>
              <v-autocomplete
                v-model="item.id_grupo_alocado"
                :items="item.grupos_linha"
                :label="$t('routePlanning.fields.selectGroup')"
                item-text="nome"
                item-value="id_grupo"
                clearable
                dense
                outlined
                hide-details
              />
            </div>

            <div class="allocation-trip-card allocation-trip-card--ida">
              <div class="allocation-trip-card__title">
                <v-icon small color="primary">mdi-arrow-right</v-icon>
                <span>{{ $t('guiasMotorista.directionIda') }}</span>
              </div>
              <v-autocomplete
                v-model="item.id_linha_ida_alocada"
                :items="linesForSense(item, 'ida')"
                :label="$t('routePlanning.fields.selectLine')"
                :item-text="lineLabel"
                item-value="id_linha"
                clearable dense outlined hide-details class="mb-2"
                @change="$emit('item-line-change', item, 'ida')"
              />
              <v-autocomplete
                v-model="item.id_cerca_embarque"
                :items="boardingFencesForItem(item)"
                :label="stopDirectionLabel('boarding', 'ida')"
                :hint="$t('routePlanning.dialogs.allocatePeopleToLines.boardingIdaHint')"
                item-text="nome_cerca"
                item-value="id_cerca"
                clearable dense outlined persistent-hint
                :disabled="item.id_linha_ida_alocada == null"
                @change="$emit('item-boarding-change', item)"
              />
              <v-tooltip bottom max-width="460">
                <template v-slot:activator="{ on, attrs }">
                  <div class="allocation-fixed-stop" v-bind="attrs" v-on="on">
                    <v-icon x-small color="grey darken-1">mdi-map-marker-check</v-icon>
                    <span class="allocation-fixed-stop__label">
                      {{ $t('passengers.personal.selectAlightingPoint') }}:
                    </span>
                    <strong>{{ fixedUnitFenceLabel(item) }}</strong>
                  </div>
                </template>
                <span>{{ fixedUnitFenceLabel(item) }}</span>
              </v-tooltip>
            </div>

            <div class="allocation-trip-card allocation-trip-card--volta">
              <div class="allocation-trip-card__title">
                <v-icon small color="deep-purple">mdi-arrow-left</v-icon>
                <span>{{ $t('guiasMotorista.directionVolta') }}</span>
              </div>
              <v-autocomplete
                v-model="item.id_linha_volta_alocada"
                :items="linesForSense(item, 'volta')"
                :label="$t('routePlanning.fields.selectLine')"
                :item-text="lineLabel"
                item-value="id_linha"
                clearable dense outlined hide-details class="mb-2"
                @change="$emit('item-line-change', item, 'volta')"
              />
              <v-tooltip bottom max-width="460">
                <template v-slot:activator="{ on, attrs }">
                  <div class="allocation-fixed-stop mb-2" v-bind="attrs" v-on="on">
                    <v-icon x-small color="grey darken-1">mdi-map-marker-check</v-icon>
                    <span class="allocation-fixed-stop__label">
                      {{ $t('passengers.personal.selectBoardingPoint') }}:
                    </span>
                    <strong>{{ fixedUnitFenceLabel(item) }}</strong>
                  </div>
                </template>
                <span>{{ fixedUnitFenceLabel(item) }}</span>
              </v-tooltip>
              <v-autocomplete
                v-model="item.id_cerca_desembarque"
                :items="returnAlightingFencesForItem(item)"
                :label="stopDirectionLabel('alighting', 'volta')"
                :hint="$t('routePlanning.dialogs.allocatePeopleToLines.alightingVoltaHint')"
                item-text="nome_cerca"
                item-value="id_cerca"
                clearable dense outlined persistent-hint
                :disabled="item.id_linha_volta_alocada == null"
                @change="$emit('item-alighting-change', item)"
              />
            </div>
          </div>
        </article>

        <v-alert v-if="!items.length" type="info" dense outlined class="ma-3">
          Nenhuma pessoa disponível para alocação.
        </v-alert>
      </div>
    </section>

    <merus-data-table
      v-else
      class="allocation-people-table"
      :headers="headers"
      :items="items"
      :loading="loading"
      item-key="id_pessoa"
      :show-select="true"
      @input="$emit('selection-change', $event)"
    >
      <template v-slot:toolbar>
        <cadastro-toolbar
          :title="$t('routePlanning.modules.alocation')"
          :token="token"
          :items="items"
          hide-search
        />
      </template>
      <template v-slot:item.id_grupo_alocado="{ item }">
        <v-autocomplete
          v-model="item.id_grupo_alocado"
          :items="item.grupos_linha"
          :label="$t('routePlanning.fields.selectGroup')"
          item-text="nome"
          item-value="id_grupo"
          clearable
          dense
          outlined
          hide-details
        />
      </template>
      <template v-slot:item.trajeto_ida="{ item }">
        <div class="allocation-trip-card allocation-trip-card--ida">
          <div class="allocation-trip-card__title">
            <v-icon small color="primary">mdi-arrow-right</v-icon>
            <span>{{ $t('guiasMotorista.directionIda') }}</span>
          </div>
          <v-autocomplete
            v-model="item.id_linha_ida_alocada"
            :items="linesForSense(item, 'ida')"
            :label="$t('routePlanning.fields.selectLine')"
            :item-text="lineLabel"
            item-value="id_linha"
            clearable
            dense
            outlined
            hide-details
            class="mb-2"
            @change="$emit('item-line-change', item, 'ida')"
          />
          <v-autocomplete
            v-model="item.id_cerca_embarque"
            :items="boardingFencesForItem(item)"
            :label="stopDirectionLabel('boarding', 'ida')"
            :hint="$t('routePlanning.dialogs.allocatePeopleToLines.boardingIdaHint')"
            item-text="nome_cerca"
            item-value="id_cerca"
            clearable
            dense
            outlined
            persistent-hint
            :disabled="item.id_linha_ida_alocada == null"
            @change="$emit('item-boarding-change', item)"
          />
          <v-tooltip bottom max-width="460">
            <template v-slot:activator="{ on, attrs }">
              <div class="allocation-fixed-stop" v-bind="attrs" v-on="on">
                <v-icon x-small color="grey darken-1">mdi-map-marker-check</v-icon>
                <span class="allocation-fixed-stop__label">
                  {{ $t('passengers.personal.selectAlightingPoint') }}:
                </span>
                <strong>{{ fixedUnitFenceLabel(item) }}</strong>
              </div>
            </template>
            <span>{{ fixedUnitFenceLabel(item) }}</span>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.trajeto_volta="{ item }">
        <div class="allocation-trip-card allocation-trip-card--volta">
          <div class="allocation-trip-card__title">
            <v-icon small color="deep-purple">mdi-arrow-left</v-icon>
            <span>{{ $t('guiasMotorista.directionVolta') }}</span>
          </div>
          <v-autocomplete
            v-model="item.id_linha_volta_alocada"
            :items="linesForSense(item, 'volta')"
            :label="$t('routePlanning.fields.selectLine')"
            :item-text="lineLabel"
            item-value="id_linha"
            clearable
            dense
            outlined
            hide-details
            class="mb-2"
            @change="$emit('item-line-change', item, 'volta')"
          />
          <v-tooltip bottom max-width="460">
            <template v-slot:activator="{ on, attrs }">
              <div class="allocation-fixed-stop mb-2" v-bind="attrs" v-on="on">
                <v-icon x-small color="grey darken-1">mdi-map-marker-check</v-icon>
                <span class="allocation-fixed-stop__label">
                  {{ $t('passengers.personal.selectBoardingPoint') }}:
                </span>
                <strong>{{ fixedUnitFenceLabel(item) }}</strong>
              </div>
            </template>
            <span>{{ fixedUnitFenceLabel(item) }}</span>
          </v-tooltip>
          <v-autocomplete
            v-model="item.id_cerca_desembarque"
            :items="returnAlightingFencesForItem(item)"
            :label="stopDirectionLabel('alighting', 'volta')"
            :hint="$t('routePlanning.dialogs.allocatePeopleToLines.alightingVoltaHint')"
            item-text="nome_cerca"
            item-value="id_cerca"
            clearable
            dense
            outlined
            persistent-hint
            :disabled="item.id_linha_volta_alocada == null"
            @change="$emit('item-alighting-change', item)"
          />
        </div>
      </template>
    </merus-data-table>
    <div v-if="!hideActions" class="d-flex justify-end mt-4">
      <v-btn color="primary" class="mr-2" :disabled="rerouteLoading" @click="$emit('cancel')">
        {{ $t('button.cancel') }}
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!canSave || rerouteLoading"
        :loading="rerouteLoading"
        @click="$emit('save')"
      >{{ $t('button.save') }}</v-btn>
    </div>
  </div>
</template>

<script>
import MerusDataTable from '@/components/Tables/MerusDataTable.vue';
import CadastroToolbar from '@/components/Toolbars/CadastroToolbar.vue';
import { isRejeitado } from '@/utils/situacoesValidacao';

export default {
  name: 'RoteirizacaoAlocacaoEditor',
  components: { MerusDataTable, CadastroToolbar },
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    headers: { type: Array, default: () => [] },
    token: { type: String, default: '' },
    bulk: { type: Object, required: true },
    selection: { type: Array, default: () => [] },
    bulkLineItems: { type: Array, default: () => [] },
    bulkLineItemsIda: { type: Array, default: () => [] },
    bulkLineItemsVolta: { type: Array, default: () => [] },
    bulkGroupItems: { type: Array, default: () => [] },
    canApplyBulk: { type: Boolean, default: false },
    fencesByLine: { type: Object, default: () => ({}) },
    destinationFenceId: { type: [Number, String], default: null },
    destinationFenceName: { type: String, default: '' },
    isRoundTripDirection: { type: Boolean, default: false },
    rerouteLoading: { type: Boolean, default: false },
    canSave: { type: Boolean, default: false },
    hideActions: { type: Boolean, default: false },
    publicationLayout: { type: Boolean, default: false },
  },
  computed: {
    selectedItemIds() {
      return new Set((this.selection || [])
        .filter((item) => item && item.id_pessoa != null)
        .map((item) => String(item.id_pessoa)));
    },
    allItemsSelected() {
      return !!this.items.length && this.items.every((item) => this.isItemSelected(item));
    },
    someItemsSelected() {
      return !this.allItemsSelected && this.items.some((item) => this.isItemSelected(item));
    },
  },
  methods: {
    isItemSelected(item) {
      return !!(item && item.id_pessoa != null
        && this.selectedItemIds.has(String(item.id_pessoa)));
    },
    isRejectedItem(item) {
      return isRejeitado(item);
    },
    hasNearestLineDistance(item) {
      if (!item) return false;
      if (item.distancia_linha_mais_proxima != null && Number.isFinite(Number(item.distancia_linha_mais_proxima))) {
        return true;
      }
      const label = item.distancia_linha_mais_proxima_extenso;
      return label != null && label !== '' && label !== '-';
    },
    nearestLineDistanceChip(item) {
      const label = item && item.distancia_linha_mais_proxima_extenso;
      if (label != null && label !== '' && label !== '-') return label;
      const meters = item && item.distancia_linha_mais_proxima;
      if (meters != null && Number.isFinite(Number(meters))) {
        return `${Math.round(Number(meters))} m`;
      }
      return '-';
    },
    hasNearestLineName(item) {
      if (!item) return false;
      const nome = item.nome_linha_mais_proxima;
      const extenso = item.linha_mais_proxima_extenso;
      return (nome != null && nome !== '' && nome !== '-')
        || (extenso != null && extenso !== '' && extenso !== '-');
    },
    nearestLineNameChip(item) {
      if (!item) return '-';
      if (item.linha_mais_proxima_extenso != null
        && item.linha_mais_proxima_extenso !== ''
        && item.linha_mais_proxima_extenso !== '-') {
        return item.linha_mais_proxima_extenso;
      }
      const cod = item.cod_linha_mais_proxima;
      const nome = item.nome_linha_mais_proxima;
      if (cod && cod !== '-' && nome && nome !== '-') return `${cod} — ${nome}`;
      if (nome && nome !== '-') return nome;
      if (cod && cod !== '-') return cod;
      return '-';
    },
    toggleItemSelection(item, selected) {
      const next = [...(this.selection || [])];
      const index = next.findIndex((current) => current && item
        && String(current.id_pessoa) === String(item.id_pessoa));
      if (selected && index < 0) next.push(item);
      if (!selected && index >= 0) next.splice(index, 1);
      this.$emit('selection-change', next);
    },
    toggleAllItems(selected) {
      this.$emit('selection-change', selected ? [...this.items] : []);
    },
    lineDirectionLabel(direction) {
      const key = direction === 'volta' ? 'guiasMotorista.directionVolta' : 'guiasMotorista.directionIda';
      return `${this.$t('routePlanning.fields.selectLine')} - ${this.$t(key)}`;
    },
    stopDirectionLabel(kind, direction) {
      const stopKey = kind === 'alighting'
        ? 'passengers.personal.selectAlightingPoint'
        : 'passengers.personal.selectBoardingPoint';
      const directionKey = direction === 'volta'
        ? 'guiasMotorista.directionVolta'
        : 'guiasMotorista.directionIda';
      return `${this.$t(stopKey)} - ${this.$t(directionKey)}`;
    },
    lineSense(line) {
      const raw = line && (line.sentido != null ? line.sentido : line.id_sentido);
      const value = Number(raw);
      return value === 1 || value === 2 ? value : null;
    },
    lineSupportsSense(line, direction) {
      const sense = this.lineSense(line);
      if (sense == null) return true;
      return direction === 'ida' ? sense === 1 : sense === 2;
    },
    lineLabel(line) {
      if (!line) return '-';
      const code = line.cod_linha || line.nome_linha || line.id_linha;
      const sense = this.lineSense(line);
      if (sense === 1) return `${code} - ${this.$t('guiasMotorista.directionIda')}`;
      if (sense === 2) return `${code} - ${this.$t('guiasMotorista.directionVolta')}`;
      return `${code} - ${this.$t('routePlanning.dialogs.allocatePeopleToLines.lineServesBothDirections')}`;
    },
    commonRoundTripLines(item) {
      const lines = Array.isArray(item && item.linhas_validadas) ? item.linhas_validadas : [];
      return lines.filter((line) => this.lineSense(line) == null);
    },
    sameRoundTripLineApplied(item) {
      if (!item || item.id_linha_ida_alocada == null || item.id_linha_volta_alocada == null) return false;
      if (String(item.id_linha_ida_alocada) !== String(item.id_linha_volta_alocada)) return false;
      return this.commonRoundTripLines(item).some(
        (line) => String(line.id_linha) === String(item.id_linha_ida_alocada),
      );
    },
    useSameLineBothDirections(item) {
      if (!item) return;
      const common = this.commonRoundTripLines(item);
      if (!common.length) return;
      const selectedId = [item.id_linha_ida_alocada, item.id_linha_volta_alocada]
        .find((id) => common.some((line) => String(line.id_linha) === String(id)));
      const idLinha = selectedId != null ? selectedId : common[0].id_linha;
      item.id_linha_ida_alocada = idLinha;
      item.id_linha_volta_alocada = idLinha;
      this.$emit('item-line-change', item, 'both');
    },
    selectedLineIds(item) {
      if (!item) return [];
      const explicit = [
        item.id_linha_ida_alocada,
        item.id_linha_volta_alocada,
      ].filter((id) => id != null && id !== '');
      const ids = explicit.length ? explicit : [item.id_linha_alocada];
      return [...new Set(ids.filter((id) => id != null && id !== '').map((id) => String(id)))];
    },
    referenceLineId(item) {
      if (!item) return null;
      return item.id_linha_ida_alocada != null
        ? item.id_linha_ida_alocada
        : (item.id_linha_volta_alocada != null ? item.id_linha_volta_alocada : item.id_linha_alocada);
    },
    hasRoundTripLine(item) {
      return this.isRoundTripDirection
        || this.selectedLineIds(item).some((id) => this.isRoundTrip(id));
    },
    linesForSense(item, direction) {
      const lines = Array.isArray(item && item.linhas_validadas) ? item.linhas_validadas : [];
      return lines.filter((line) => this.lineSupportsSense(line, direction));
    },
    fencesForLine(idLinha) {
      if (idLinha == null || idLinha === '') return [];
      return this.fencesByLine[String(idLinha)] || [];
    },
    fencesForItem(item) {
      const byId = new Map();
      this.selectedLineIds(item).forEach((id) => {
        this.fencesForLine(id).forEach((fence) => {
          if (fence && fence.id_cerca != null && !byId.has(String(fence.id_cerca))) {
            byId.set(String(fence.id_cerca), fence);
          }
        });
      });
      return [...byId.values()];
    },
    boardingFencesForItem(item) {
      const idLinha = item && item.id_linha_ida_alocada != null
        ? item.id_linha_ida_alocada
        : this.referenceLineId(item);
      return this.selectableFencesForLine(idLinha, 'ida');
    },
    returnAlightingFencesForItem(item) {
      const idLinha = item && item.id_linha_volta_alocada != null
        ? item.id_linha_volta_alocada
        : this.referenceLineId(item);
      return this.selectableFencesForLine(idLinha, 'volta');
    },
    fenceSupportsDirection(fence, direction) {
      const sense = Number(fence && fence.id_sentido);
      if (!Number.isFinite(sense) || sense === -1) return true;
      return direction === 'volta'
        ? sense === 2 || sense === 5 || sense === 6
        : sense === 1 || sense === 3 || sense === 4;
    },
    selectableFencesForLine(idLinha, direction) {
      const destinationId = this.destinationFenceId != null
        ? String(this.destinationFenceId)
        : null;
      const useBothDirections = this.isRoundTrip(idLinha);
      return this.fencesForLine(idLinha).filter((fence) => (
        (!destinationId || String(fence.id_cerca) !== destinationId)
        && (useBothDirections || this.fenceSupportsDirection(fence, direction))
      ));
    },
    fenceOptionById(item, idCerca) {
      if (idCerca == null || idCerca === '') return [];
      const found = this.fencesForItem(item).find(
        (fence) => fence && String(fence.id_cerca) === String(idCerca)
      );
      return [found || { id_cerca: idCerca, nome_cerca: String(idCerca) }];
    },
    fixedUnitFenceItems(item) {
      return this.fenceOptionById(item, this.destinationFenceId);
    },
    fenceLabelById(item, idCerca) {
      if (idCerca == null || idCerca === '') return '-';
      const option = this.fenceOptionById(item, idCerca)[0];
      if (!option) return String(idCerca);
      return option.nome_cerca || option.endereco || option.cod_cerca || String(idCerca);
    },
    fixedUnitFenceLabel(item) {
      return this.destinationFenceName || this.fenceLabelById(item, this.destinationFenceId);
    },
    alightingFencesForLine(idLinha) {
      const fences = this.fencesForLine(idLinha);
      const destId = this.destinationFenceId != null ? String(this.destinationFenceId) : null;
      if (!destId) return fences;
      return fences.filter((f) => String(f.id_cerca) !== destId);
    },
    alightingFencesForItem(item) {
      const destId = this.destinationFenceId != null ? String(this.destinationFenceId) : null;
      return this.fencesForItem(item).filter((f) => !destId || String(f.id_cerca) !== destId);
    },
    isRoundTrip(idLinha) {
      const fences = this.fencesForLine(idLinha);
      let hasIda = false;
      let hasVolta = false;
      for (const f of fences) {
        const s = Number(f && f.id_sentido);
        if (!Number.isFinite(s)) continue;
        if (s === -1) return true;
        if (s === 1 || s === 3 || s === 4) hasIda = true;
        if (s === 2 || s === 5 || s === 6) hasVolta = true;
        if (hasIda && hasVolta) return true;
      }
      return this.isRoundTripDirection;
    },
  },
};
</script>

<style scoped>
.allocation-publication-list {
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  background: #f7f8fa;
}

.allocation-publication-list__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
}

.allocation-publication-list__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
}

.allocation-person-card {
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.14);
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.allocation-person-card--selected {
  border-color: rgba(25, 118, 210, 0.55);
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.12);
}

.allocation-person-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: #fafafa;
}

.allocation-person-card__identity {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 140px;
}

.allocation-person-card__identity strong,
.allocation-person-card__identity span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.allocation-person-card__identity span {
  color: rgba(0, 0, 0, 0.58);
  font-size: 0.75rem;
}

.allocation-person-card__meta {
  display: flex;
  flex: 0 1 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.allocation-person-card__content {
  display: grid;
  grid-template-columns: minmax(180px, 0.65fr) repeat(2, minmax(340px, 1fr));
  align-items: start;
  gap: 14px;
  padding: 14px;
}

.allocation-person-card__group {
  min-width: 180px;
}

.allocation-round-trip-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 14px;
  border-bottom: 1px solid rgba(25, 118, 210, 0.14);
  background: #f5f9ff;
}

.allocation-round-trip-action__text {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 7px;
  color: rgba(0, 0, 0, 0.68);
  font-size: 0.78rem;
}

.allocation-round-trip-action--applied {
  border-bottom-color: rgba(76, 175, 80, 0.2);
  background: #f4fbf5;
}

.allocation-field-heading {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 24px;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.72);
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
}

.allocation-trip-card {
  min-width: 250px;
  padding: 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: #fff;
}

.allocation-publication-list .allocation-trip-card {
  min-width: 340px;
}

.allocation-trip-card--ida {
  border-left: 4px solid #1976d2;
}

.allocation-trip-card--volta {
  border-left: 4px solid #673ab7;
}

.allocation-trip-card__title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  color: rgba(0, 0, 0, 0.72);
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
}

.allocation-fixed-stop {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 5px;
  padding: 7px 8px;
  border-radius: 5px;
  background: #f5f5f5;
  color: rgba(0, 0, 0, 0.76);
  font-size: 0.76rem;
  line-height: 1.25;
  cursor: help;
}

.allocation-fixed-stop__label {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.56);
}

.allocation-fixed-stop strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.allocation-people-table >>> table {
  min-width: 1120px;
  table-layout: fixed;
}

.allocation-people-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.allocation-people-table >>> .v-data-table-header th {
  padding: 8px !important;
  white-space: normal;
}

.allocation-people-table >>> tbody td {
  height: auto !important;
  padding: 8px !important;
  vertical-align: top;
  white-space: normal;
  overflow-wrap: anywhere;
}

.allocation-people-table >>> .v-text-field--outlined.v-input--dense .v-input__slot {
  min-height: 36px;
}

@media (max-width: 1180px) {
  .allocation-person-card__content {
    grid-template-columns: repeat(2, minmax(340px, 1fr));
  }

  .allocation-person-card__group {
    grid-column: 1 / -1;
  }
}

@media (max-width: 820px) {
  .allocation-person-card__content {
    grid-template-columns: minmax(0, 1fr);
  }

  .allocation-person-card__group {
    grid-column: auto;
  }

  .allocation-publication-list .allocation-trip-card {
    min-width: 0;
  }

  .allocation-round-trip-action {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
